import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { X } from 'lucide-react'
import { fetchLoops, type ApiLoop } from '../../lib/api'
import { createLoop, updateLoop, deleteLoop, setLoopEnabled, startLoopNow, breakLoopNow, type LoopWrite } from '../../lib/loopsApi'
import { useAgentsList } from './useAgents'
import { Panel, Spinner, ErrorState, EmptyState, Badge, Button, Table, type Column } from '../../components/ui'
import { fmtDateTime } from '../../lib/format'
import { useActiveWorkspace } from '../../lib/workspace'

type Frequency = 'daily' | 'weekly' | 'monthly'

const WEEKDAYS: { value: number; label: string }[] = [
  { value: 1, label: 'Mon' }, { value: 2, label: 'Tue' }, { value: 3, label: 'Wed' },
  { value: 4, label: 'Thu' }, { value: 5, label: 'Fri' }, { value: 6, label: 'Sat' }, { value: 0, label: 'Sun' },
]
const WD_LABEL = (d: number) => WEEKDAYS.find((w) => w.value === d)?.label ?? String(d)

const TIMEZONES = [
  'UTC', 'Europe/Warsaw', 'Europe/London', 'Europe/Berlin', 'Europe/Paris',
  'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
  'Asia/Tokyo', 'Asia/Shanghai', 'Asia/Singapore', 'Asia/Kolkata', 'Australia/Sydney',
]

const pad = (n: number) => String(n).padStart(2, '0')
const hhmm = (h: number, m: number) => `${pad(h)}:${pad(m)}`

function whenLabel(l: ApiLoop): string {
  const at = hhmm(l.hour, l.minute)
  if (l.frequency === 'daily') return `daily at ${at}`
  if (l.frequency === 'weekly') return `weekly on ${l.day_of_week != null ? WD_LABEL(l.day_of_week) : '?'} at ${at}`
  if (l.frequency === 'monthly') return `monthly on day ${l.day_of_month ?? '?'} at ${at}`
  return 'custom schedule'
}

function stopLabel(l: ApiLoop): string {
  const parts = [`${l.max_runs} runs`]
  if (l.max_tokens != null) parts.push(`${l.max_tokens.toLocaleString()} tokens`)
  if (l.idle_break) parts.push('idle')
  return parts.join(' · ')
}

const BREAK_LABELS: Record<string, string> = {
  max_runs: 'run limit reached',
  max_tokens: 'token limit reached',
  idle: 'nothing left to do',
  run_failed: 'a run failed',
  manual: 'stopped manually',
  disabled: 'loop was disabled',
}

export function LoopsTab({ active }: { active: boolean }) {
  const qc = useQueryClient()
  const { activeWorkspaceKey } = useActiveWorkspace()
  const { data, isLoading, isError } = useQuery({
    queryKey: ['loops', activeWorkspaceKey],
    queryFn: () => fetchLoops(activeWorkspaceKey),
    enabled: active && !!activeWorkspaceKey,
    refetchInterval: active ? 5000 : false,
  })
  const [editing, setEditing] = useState<ApiLoop | 'new' | null>(null)
  const [busyId, setBusyId] = useState<string | null>(null)

  if (!activeWorkspaceKey) return <EmptyState title="No active workspace." />
  if (isLoading) return <Spinner />
  if (isError || !data) return <ErrorState message="Couldn't load loops." />

  const loops = data.loops
  const showOwner = loops.some((l) => l.created_by || l.user_id)
  const invalidate = () => qc.invalidateQueries({ queryKey: ['loops', activeWorkspaceKey] })

  const act = async (l: ApiLoop, fn: () => Promise<void>, fallback: string) => {
    setBusyId(l.id)
    try {
      await fn()
      invalidate()
    } catch (e) {
      alert(e instanceof Error ? e.message : fallback)
    } finally {
      setBusyId(null)
    }
  }

  const onToggle = (l: ApiLoop) => act(l, () => setLoopEnabled(activeWorkspaceKey, l.id, !l.enabled), 'Update failed')
  const onStart = (l: ApiLoop) => act(l, () => startLoopNow(activeWorkspaceKey, l.id), 'Start failed')
  const onBreak = (l: ApiLoop) => {
    if (!confirm(`Stop the loop for ${l.agent_name}? The current run will finish, but no new runs will start.`)) return
    void act(l, () => breakLoopNow(activeWorkspaceKey, l.id), 'Stop failed')
  }
  const onDelete = (l: ApiLoop) => {
    if (!confirm(`Delete loop — ${l.agent_name} ${whenLabel(l)}?`)) return
    void act(l, () => deleteLoop(activeWorkspaceKey, l.id), 'Delete failed')
  }

  const columns: Column<ApiLoop>[] = [
    { key: 'agent', header: 'Agent', render: (l) => <span className="font-medium">{l.agent_name}</span> },
    ...(showOwner ? [{ key: 'owner', header: 'Owner', render: (l: ApiLoop) => <span className="text-xs text-gray-500">{l.created_by || (l.user_id ? l.user_id.slice(0, 8) : 'Global')}</span> } satisfies Column<ApiLoop>] : []),
    { key: 'when', header: 'Starts', render: (l) => <span className="text-gray-700">{whenLabel(l)}</span> },
    { key: 'stop', header: 'Stops after', render: (l) => <span className="text-xs text-gray-500">{stopLabel(l)}</span> },
    { key: 'status', header: 'Status', render: (l) => (
      <Badge tone={l.status === 'active' ? 'indigo' : 'neutral'}>
        {l.status === 'active' ? 'running' : l.enabled ? 'idle' : 'disabled'}
      </Badge>
    ) },
    { key: 'progress', header: 'Progress', render: (l) => (
      <span className="text-xs text-gray-500">
        {l.status === 'active'
          ? `run ${l.runs_done}/${l.max_runs} · ${l.tokens_used.toLocaleString()} tokens`
          : l.last_break_reason
            ? `last: ${BREAK_LABELS[l.last_break_reason] ?? l.last_break_reason}`
            : '—'}
      </span>
    ) },
    { key: 'next', header: 'Next start', render: (l) => (
      <span className="text-xs text-gray-500">{l.enabled ? fmtDateTime(l.next_run_at) : '—'}</span>
    ) },
    { key: 'actions', header: '', render: (l) => (
      <div className="flex gap-2 justify-end">
        {l.can_edit && (
          <>
            {l.status === 'active' ? (
              <Button variant="secondary" disabled={busyId === l.id} onClick={() => onBreak(l)} className="!text-red-600">Stop</Button>
            ) : (
              <Button variant="primary" disabled={busyId === l.id} onClick={() => onStart(l)}>Start now</Button>
            )}
            <Button variant="secondary" onClick={() => setEditing(l)}>Edit</Button>
            <Button variant="secondary" disabled={busyId === l.id} onClick={() => onToggle(l)} className={l.enabled ? '!text-red-600' : ''}>
              {l.enabled ? 'Disable' : 'Enable'}
            </Button>
            <Button variant="secondary" disabled={busyId === l.id} onClick={() => onDelete(l)} className="!text-red-600">Delete</Button>
          </>
        )}
      </div>
    ) },
  ]

  return (
    <div className="flex flex-col gap-3">
      <div className="rounded-lg border border-indigo-100 bg-indigo-50/60 px-4 py-3 text-sm text-gray-700">
        A loop starts on its schedule and keeps running the agent back-to-back until a stop condition
        is met — a run limit, a token budget, or the agent having nothing left to do. Each run shows
        up under Agent runs.
      </div>
      <div className="flex justify-start">
        <Button variant="primary" onClick={() => setEditing('new')}>Create new agent loop</Button>
      </div>
      <Panel title={`Agent loops — ${loops.length}`}>
        {loops.length === 0 ? (
          <EmptyState title="No loops yet." hint="Create one to run an agent until it's done." />
        ) : (
          <Table columns={columns} rows={loops} empty="No loops yet." />
        )}
      </Panel>
      {editing && (
        <LoopModal
          loop={editing === 'new' ? null : editing}
          workspaceKey={activeWorkspaceKey}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); invalidate() }}
        />
      )}
    </div>
  )
}

function LoopModal({ loop, workspaceKey, onClose, onSaved }: { loop: ApiLoop | null; workspaceKey: string; onClose: () => void; onSaved: () => void }) {
  const { data: agents } = useAgentsList()
  const [agentName, setAgentName] = useState(loop?.agent_name ?? '')
  const [frequency, setFrequency] = useState<Frequency>((loop?.frequency as Frequency) ?? 'daily')
  const [timezone, setTimezone] = useState(loop?.timezone ?? 'Europe/Warsaw')
  const [time, setTime] = useState(loop ? hhmm(loop.hour, loop.minute) : '08:00')
  const [weekday, setWeekday] = useState(loop?.day_of_week ?? 1)
  const [monthdayText, setMonthdayText] = useState(String(loop?.day_of_month ?? 1))
  const [maxRunsText, setMaxRunsText] = useState(String(loop?.max_runs ?? 5))
  const [maxTokensText, setMaxTokensText] = useState(loop?.max_tokens != null ? String(loop.max_tokens) : '')
  const [idleBreak, setIdleBreak] = useState(loop?.idle_break ?? false)
  const [saving, setSaving] = useState(false)

  const buildSpec = (): LoopWrite | string => {
    if (!agentName.trim()) return 'Pick an agent.'
    if (!/^\d{2}:\d{2}$/.test(time)) return 'Pick a start time.'
    const [hour, minute] = time.split(':').map(Number)
    const maxRuns = Number(maxRunsText)
    if (!Number.isInteger(maxRuns) || maxRuns < 1) return 'Max runs must be a whole number of at least 1.'
    let maxTokens: number | null = null
    if (maxTokensText.trim() !== '') {
      maxTokens = Number(maxTokensText)
      if (!Number.isInteger(maxTokens) || maxTokens < 1) return 'Max tokens must be a whole number of at least 1.'
    }
    const spec: LoopWrite = {
      workspace_key: workspaceKey,
      agent_name: agentName.trim(),
      frequency,
      hour,
      minute,
      timezone,
      max_runs: maxRuns,
      max_tokens: maxTokens,
      idle_break: idleBreak,
    }
    if (frequency === 'weekly') spec.day_of_week = weekday
    if (frequency === 'monthly') {
      const d = Number(monthdayText.trim())
      if (!Number.isInteger(d) || d < 1 || d > 31) return 'Enter a day of month between 1 and 31.'
      spec.day_of_month = d
    }
    return spec
  }

  const onSave = async () => {
    const spec = buildSpec()
    if (typeof spec === 'string') {
      alert(spec)
      return
    }
    setSaving(true)
    try {
      if (loop) await updateLoop(loop.id, spec)
      else await createLoop(spec)
      onSaved()
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
      <div className="bg-white rounded-lg shadow-xl w-[min(560px,90vw)] max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <h3 className="text-sm font-semibold text-gray-900">{loop ? 'Edit agent loop' : 'Create new agent loop'}</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-800"><X size={16} /></button>
        </div>
        <div className="flex-1 overflow-auto p-4 flex flex-col gap-3">
          <label className="text-xs text-gray-700 flex flex-col gap-1">
            Agent
            <select value={agentName} onChange={(e) => setAgentName(e.target.value)} className="px-2 py-1.5 text-xs border border-gray-300 rounded-md">
              <option value="">Select…</option>
              {(agents ?? []).map((a) => (<option key={a.name} value={a.name}>{a.name}</option>))}
            </select>
          </label>
          <label className="text-xs text-gray-700 flex flex-col gap-1">
            Frequency
            <select value={frequency} onChange={(e) => setFrequency(e.target.value as Frequency)} className="px-2 py-1.5 text-xs border border-gray-300 rounded-md">
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
            </select>
          </label>
          {frequency === 'weekly' && (
            <div className="text-xs text-gray-700 flex flex-col gap-1">
              Day of week
              <div className="flex flex-wrap gap-1">
                {WEEKDAYS.map((d) => (
                  <button
                    key={d.value}
                    type="button"
                    onClick={() => setWeekday(d.value)}
                    className={`px-2 py-1 rounded-md border text-xs ${weekday === d.value ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-700 border-gray-300'}`}
                  >
                    {d.label}
                  </button>
                ))}
              </div>
            </div>
          )}
          {frequency === 'monthly' && (
            <label className="text-xs text-gray-700 flex flex-col gap-1">
              Day of month (1–31)
              <input value={monthdayText} onChange={(e) => setMonthdayText(e.target.value)} placeholder="1" className="px-2 py-1.5 text-xs border border-gray-300 rounded-md" />
            </label>
          )}
          <label className="text-xs text-gray-700 flex flex-col gap-1">
            Start time
            <input type="time" value={time} onChange={(e) => setTime(e.target.value)} className="px-2 py-1.5 text-xs border border-gray-300 rounded-md self-start" />
          </label>
          <label className="text-xs text-gray-700 flex flex-col gap-1">
            Timezone
            <select value={timezone} onChange={(e) => setTimezone(e.target.value)} className="px-2 py-1.5 text-xs border border-gray-300 rounded-md">
              {TIMEZONES.map((tz) => (<option key={tz} value={tz}>{tz}</option>))}
            </select>
          </label>
          <label className="text-xs text-gray-700 flex flex-col gap-1">
            Max runs per loop
            <input value={maxRunsText} onChange={(e) => setMaxRunsText(e.target.value)} placeholder="5" className="px-2 py-1.5 text-xs border border-gray-300 rounded-md" />
          </label>
          <label className="text-xs text-gray-700 flex flex-col gap-1">
            Max tokens per loop (optional)
            <input value={maxTokensText} onChange={(e) => setMaxTokensText(e.target.value)} placeholder="no limit" className="px-2 py-1.5 text-xs border border-gray-300 rounded-md" />
          </label>
          <label className="text-xs text-gray-700 flex items-center gap-2">
            <input type="checkbox" checked={idleBreak} onChange={(e) => setIdleBreak(e.target.checked)} />
            Stop early when a run produces no new research activity
          </label>
        </div>
        <div className="flex justify-end gap-2 px-4 py-3 border-t border-gray-200">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button disabled={saving} onClick={onSave}>{saving ? 'Saving…' : 'Save'}</Button>
        </div>
      </div>
    </div>
  )
}
